import { useCallback, useEffect, useMemo, useState } from "react";
import type { Row, TabDef, AcaoLinha } from "../types";
import { api, ApiError } from "../api";
import { formatCell } from "../format";
import { cargoEhDoOperacional, indexarCargos, type IndiceCargos } from "../cargos-operacional";
import { usePodeExcluir } from "../permissoes";
import { ordenarPorNome, ordenarRegistros } from "../ordem";
import RecordForm from "./RecordForm";
import ErrorState from "./ErrorState";
import SugerirTreinosModal from "./SugerirTreinosModal";
import SugerirEngajamentoModal from "./SugerirEngajamentoModal";
import ConcluirTreinoModal from "./ConcluirTreinoModal";
import PastaModal from "./PastaModal";
import FichaModal from "./FichaModal";
import CertificadoTreinoModal from "./CertificadoTreinoModal";
import GerarLinkAvaliacaoModal from "./GerarLinkAvaliacaoModal";
import ConsolidadoAvaliacoesModal from "./ConsolidadoAvaliacoesModal";
import AvaliacoesColaboradorModal from "./AvaliacoesColaboradorModal";
import ModelosPromocaoModal from "./ModelosPromocaoModal";
import ImportarColaboradoresModal from "./ImportarColaboradoresModal";
import AplicarAvaliacaoModal from "./AplicarAvaliacaoModal";
import RoteiroEntrevistaModal from "./RoteiroEntrevistaModal";
import CurriculoCandidatoModal from "./CurriculoCandidato";
import ConviteEntrevistaModal from "./ConviteEntrevistaModal";
import RetornoEntrevistaModal from "./RetornoEntrevistaModal";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconSparkles,
  IconFolder,
  IconCheck,
  IconAward,
  IconLink,
  IconUser,
  IconStar,
  IconFile,
  IconTrendUp,
  IconDownload,
  IconUpload,
  IconClose,
  IconColumns,
  IconClipboard,
  IconEye,
  IconChat,
} from "../icons";

/** Metadados dos botões de ação extra por linha (definidos em tab.acoesLinha). */
const ACAO_META: Record<
  AcaoLinha,
  { title: string; Icon: typeof IconSparkles; cls: string }
> = {
  treinoIa: { title: "Sugerir treinamentos (IA)", Icon: IconSparkles, cls: "icon-btn-ia" },
  ficha: { title: "Ficha 360° do colaborador", Icon: IconUser, cls: "icon-btn-folder" },
  pasta: { title: "Pasta do colaborador", Icon: IconFolder, cls: "icon-btn-folder" },
  concluir: { title: "Concluir treinamento", Icon: IconCheck, cls: "icon-btn-ok" },
  engajIa: { title: "Sugerir ações de melhoria de clima (IA)", Icon: IconSparkles, cls: "icon-btn-ia" },
  certificado: { title: "Certificado do colaborador", Icon: IconAward, cls: "icon-btn-folder" },
  documentos: { title: "Documentos do candidato (currículo, RG, CPF…)", Icon: IconFile, cls: "icon-btn-folder" },
  convite: {
    title: "Convite do candidato (dia, horário, endereço e quem procurar na recepção)",
    Icon: IconLink,
    cls: "icon-btn-ia",
  },
  retorno: { title: "Retorno ao candidato (WhatsApp)", Icon: IconChat, cls: "icon-btn-ok" },
  roteiro: { title: "Roteiro de entrevista (conduzir e registrar)", Icon: IconClipboard, cls: "icon-btn-ok" },
  curriculo: {
    title: "Currículo, respostas da triagem e DISC do candidato",
    Icon: IconEye,
    cls: "icon-btn-folder",
  },
  avaliar: { title: "Avaliação de desempenho (formulário FO 094)", Icon: IconStar, cls: "icon-btn-ok" },
  avaliacoes: {
    title: "Ver as avaliações de desempenho já realizadas",
    Icon: IconEye,
    cls: "icon-btn-folder",
  },
};

interface Props {
  tab: TabDef;
  colaboradores: Row[];
  onDataChanged: () => void; // avisa o App para recarregar colaboradores/dashboard
}

export default function CrudView({ tab, colaboradores, onDataChanged }: Props) {
  const podeExcluir = usePodeExcluir(); // só admin apaga registro (regra do Portal)
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Row | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [acao, setAcao] = useState<{ tipo: AcaoLinha; row: Row } | null>(null);
  const [gerarLink, setGerarLink] = useState(false); // modal "gerar link p/ gestor" (aba Avaliações)
  const [consolidado, setConsolidado] = useState(false); // modal consolidado (aba Avaliações)
  const [modelos, setModelos] = useState(false); // guia dos modelos (aba Promoções)
  const [importar, setImportar] = useState(false); // planilha (aba Colaboradores)
  const [escolherColunas, setEscolherColunas] = useState(false);
  const [aplicarAvaliacao, setAplicarAvaliacao] = useState(false); // FO 094 (aba Avaliações)
  const [avisoPlanilha, setAvisoPlanilha] = useState<string | null>(null);
  // Tabela de Cargos e Salários: diz o departamento de cada cargo e, com isso,
  // quem tem competências no cadastro (só o pessoal do Operacional).
  const [indiceCargos, setIndiceCargos] = useState<IndiceCargos | null>(null);

  /**
   * COLUNAS DA LISTAGEM.
   *
   * O cadastro tem muito mais campo do que cabe na tela (a ficha do
   * colaborador tem 30), então a listagem começa pelas colunas do dia a dia e
   * cada pessoa escolhe o que mais quer ver — a escolha fica gravada NESTE
   * navegador, por aba.
   */
  const padraoColunas = useMemo(
    () => tab.fields.filter((f) => !f.hideInTable).map((f) => f.name),
    [tab.fields],
  );
  const temColunasExtras = useMemo(() => tab.fields.some((f) => f.hideInTable), [tab.fields]);
  const chaveColunas = `rh:colunas:${tab.key}`;

  const [colunas, setColunas] = useState<string[]>(padraoColunas);

  useEffect(() => {
    let salvas: string[] | null = null;
    try {
      const bruto = localStorage.getItem(chaveColunas);
      if (bruto) salvas = JSON.parse(bruto);
    } catch {
      /* preferência corrompida: volta para o padrão */
    }
    // Campo que saiu da configuração não pode continuar sendo pedido.
    const validas = Array.isArray(salvas)
      ? salvas.filter((n) => tab.fields.some((f) => f.name === n))
      : null;
    setColunas(validas && validas.length > 0 ? validas : padraoColunas);
  }, [chaveColunas, padraoColunas, tab.fields]);

  function definirColunas(nomes: string[]) {
    setColunas(nomes);
    try {
      localStorage.setItem(chaveColunas, JSON.stringify(nomes));
    } catch {
      /* sem localStorage (aba anônima): vale só para esta sessão */
    }
  }

  /** Na ordem em que os campos foram declarados, não na ordem dos cliques. */
  const colunasVisiveis = useMemo(
    () => tab.fields.filter((f) => colunas.includes(f.name)),
    [tab.fields, colunas],
  );

  const usaSkills = useMemo(() => tab.fields.some((f) => f.type === "skills"), [tab.fields]);

  useEffect(() => {
    if (!usaSkills) return;
    let cancelado = false;
    api
      // Com as arquivadas: cadastro antigo ("Eletricista - Pleno") ainda é do
      // Operacional, mesmo que o cargo tenha saído do plano vigente.
      .listTodos("cargos")
      .then((rows) => {
        if (!cancelado) setIndiceCargos(indexarCargos(rows));
      })
      .catch(() => {
        /* sem a tabela de cargos o quadro de competências fica oculto */
      });
    return () => {
      cancelado = true;
    };
  }, [usaSkills]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRows(await api.list(tab.resource));
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
    } finally {
      setLoading(false);
    }
  }, [tab.resource]);

  useEffect(() => {
    setSearch("");
    load();
  }, [load]);

  /**
   * OS RECORTES DA BARRA (ver `filtros` em src/config.ts).
   *
   * Um valor por campo; vazio = "todos". A escolha fica guardada NESTE
   * navegador, por aba — quem trabalha o dia inteiro com o quadro ativo não
   * remarca o filtro a cada visita. O contador ao lado sempre diz "X de Y",
   * então a lista encurtada nunca é uma surpresa silenciosa.
   */
  const chaveFiltros = `rh:filtros:${tab.key}`;
  const [filtros, setFiltros] = useState<Record<string, string>>({});

  useEffect(() => {
    let salvos: Record<string, string> = {};
    try {
      const bruto = localStorage.getItem(chaveFiltros);
      if (bruto) salvos = JSON.parse(bruto) ?? {};
    } catch {
      /* preferência corrompida: começa sem filtro */
    }
    // Filtro que saiu da configuração não pode continuar sendo aplicado —
    // senão a lista viria cortada por um campo que ninguém vê mais.
    const validos: Record<string, string> = {};
    for (const f of tab.filtros ?? []) {
      const v = salvos[f.campo];
      if (typeof v === "string" && v) validos[f.campo] = v;
    }
    setFiltros(validos);
  }, [chaveFiltros, tab.filtros]);

  function definirFiltro(campo: string, valor: string) {
    setFiltros((atual) => {
      const novo = { ...atual };
      if (valor) novo[campo] = valor;
      else delete novo[campo];
      try {
        localStorage.setItem(chaveFiltros, JSON.stringify(novo));
      } catch {
        /* navegador sem armazenamento: o filtro vale só nesta visita */
      }
      return novo;
    });
  }

  /** As opções de cada seletor: as do campo MAIS as que já estão nos dados. */
  const opcoesDoFiltro = useCallback(
    (campo: string): string[] => {
      const doCampo = tab.fields.find((f) => f.name === campo)?.options ?? [];
      // Valor que existe no banco mas não está no cadastro (importação, versão
      // antiga) precisa aparecer: senão não há como enxergar esses registros.
      const nosDados = [
        ...new Set(rows.map((r) => String(r[campo] ?? "").trim()).filter(Boolean)),
      ];
      return [...doCampo, ...nosDados.filter((v) => !doCampo.includes(v))];
    },
    [tab.fields, rows],
  );

  /** Algum registro está SEM valor neste campo? (aí cabe o "Sem informação".) */
  const temSemValor = useCallback(
    (campo: string) => rows.some((r) => String(r[campo] ?? "").trim() === ""),
    [rows],
  );

  /** Marca especial: o registro sem valor nenhum no campo do filtro. */
  const SEM_VALOR = "__sem__";

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const lista = rows.filter((r) => {
      for (const [campo, valor] of Object.entries(filtros)) {
        const atual = String(r[campo] ?? "").trim();
        if (valor === SEM_VALOR ? atual !== "" : atual !== valor) return false;
      }
      if (!q) return true;
      return tab.fields.some((f) => {
        const value = f.ref === "colaboradores" ? r.colaborador_nome : r[f.name];
        return String(value ?? "").toLowerCase().includes(q);
      });
    });
    // Cadastro por nome (ex.: Colaboradores): A→Z na tela, venha a API como vier.
    const campoNome = tab.ordenarPorNome;
    if (campoNome) return ordenarPorNome(lista, (r) => r[campoNome]);
    // Aba de LANÇAMENTOS de colaborador (plano de ação, contratações, demissões,
    // promoções, avaliações, clima): A→Z pelo colaborador e, dentro dele, data
    // crescente. A API continua mandando por id; a ordem é decidida aqui.
    if (tab.fields.some((f) => f.ref === "colaboradores")) {
      const data = tab.fields.find((f) => f.type === "date")?.name;
      return ordenarRegistros(lista, { nome: "colaborador_nome", data });
    }
    return lista;
  }, [rows, search, filtros, tab.fields, tab.ordenarPorNome]);

  function openNew() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(row: Row) {
    setEditing(row);
    setFormOpen(true);
  }

  async function handleSubmit(data: Row) {
    if (editing) {
      await api.update(tab.resource, editing.id, data);
    } else {
      await api.create(tab.resource, data);
    }
    setFormOpen(false);
    await load();
    onDataChanged();
  }

  async function refreshAll() {
    await load();
    onDataChanged();
  }

  async function handleDelete(row: Row) {
    if (!confirm("Tem certeza que deseja excluir este registro?")) return;
    try {
      await api.remove(tab.resource, row.id);
      await load();
      onDataChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Erro ao excluir");
    }
  }

  if (error)
    return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {/*
          Os recortes da aba (ex.: Situação = Ativo). Ficam ao lado da busca:
          a busca acha UMA pessoa, o filtro mostra um GRUPO.
        */}
        {(tab.filtros ?? []).map((f) => {
          const campo = tab.fields.find((x) => x.name === f.campo);
          const escolhido = filtros[f.campo] ?? "";
          /*
           * BOTÕES, NÃO UM <select>.
           *
           * A lista de um select é desenhada pelo SISTEMA OPERACIONAL: abria
           * branca, com a seleção azul do Windows, no meio de uma tela verde
           * escura. Além de destoar, escondia as opções atrás de um clique.
           *
           * Aqui as opções ficam à vista, cada uma com QUANTOS registros tem —
           * dá para ver que são 38 ativos e 47 inativos antes de clicar.
           */
          const opcoes = [
            { valor: "", rotulo: f.vazioLabel ?? "Todos" },
            ...opcoesDoFiltro(f.campo).map((o) => ({ valor: o, rotulo: o })),
            ...(temSemValor(f.campo)
              ? [{ valor: SEM_VALOR, rotulo: "Sem informação" }]
              : []),
          ];
          return (
            <div key={f.campo} className="crud-filtro" role="group" aria-label={f.label ?? f.campo}>
              <span className="crud-filtro-rotulo">{f.label ?? campo?.label ?? f.campo}</span>
              <div className="crud-chips">
                {opcoes.map((o) => {
                  const quantos =
                    o.valor === ""
                      ? rows.length
                      : rows.filter((r) => {
                          const atual = String(r[f.campo] ?? "").trim();
                          return o.valor === SEM_VALOR ? atual === "" : atual === o.valor;
                        }).length;
                  const ativo = escolhido === o.valor;
                  return (
                    <button
                      key={o.valor || "__todos__"}
                      type="button"
                      className={`crud-chip ${ativo ? "on" : ""}`}
                      data-vazio={quantos === 0 ? "1" : "0"}
                      aria-pressed={ativo}
                      title={`${o.rotulo}: ${quantos} registro(s)`}
                      onClick={() => definirFiltro(f.campo, o.valor)}
                    >
                      {o.rotulo}
                      <em className="crud-chip-num">{quantos}</em>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
        <div className="toolbar-right">
          {/*
            Com filtro ligado, o contador mostra "X de Y" — a lista encurtada
            precisa dizer que está encurtada, senão vira "sumiram pessoas".
          */}
          <span className="badge">
            {Object.keys(filtros).length > 0
              ? `${filtered.length} de ${rows.length} registro(s)`
              : `${filtered.length} registro(s)`}
          </span>
          {tab.avaliacaoLink && (
            <button className="btn-secondary" onClick={() => setConsolidado(true)}>
              <IconStar size={17} /> Consolidado
            </button>
          )}
          {tab.avaliacaoLink && (
            <button className="btn-secondary" onClick={() => setGerarLink(true)}>
              <IconLink size={17} /> Gerar link p/ gestor
            </button>
          )}
          {tab.avaliacaoLink && (
            <button className="btn-primary" onClick={() => setAplicarAvaliacao(true)}>
              <IconStar size={17} /> Avaliar (FO 094)
            </button>
          )}
          {tab.modelosPromocao && (
            <button className="btn-secondary" onClick={() => setModelos(true)}>
              <IconTrendUp size={17} /> Modelos de promoção
            </button>
          )}
          {temColunasExtras && (
            <button
              className="btn-secondary"
              onClick={() => setEscolherColunas(true)}
              title="Escolher as colunas da lista"
            >
              <IconColumns size={17} /> Colunas ({colunasVisiveis.length}/{tab.fields.length})
            </button>
          )}
          {tab.planilha && (
            <>
              <button
                className="btn-secondary"
                title="Baixar todos os colaboradores em Excel"
                onClick={() =>
                  api.planilha
                    .baixar("exportar")
                    .catch((e) => alert(e instanceof Error ? e.message : "Erro ao exportar."))
                }
              >
                <IconDownload size={17} /> Exportar Excel
              </button>
              <button className="btn-secondary" onClick={() => setImportar(true)}>
                <IconUpload size={17} /> Importar Excel
              </button>
            </>
          )}
          <button className="btn-primary" onClick={openNew}>
            <IconPlus size={17} /> Novo
          </button>
        </div>
      </div>

      {avisoPlanilha && (
        <div className="endo-aviso">
          <span>{avisoPlanilha}</span>
          <button className="icon-btn" title="Fechar" onClick={() => setAvisoPlanilha(null)}>
            <IconClose size={16} />
          </button>
        </div>
      )}

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando...</div>
        ) : filtered.length === 0 ? (
          <div className="empty">Nenhum registro encontrado.</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                {colunasVisiveis.map((f) => (
                  <th key={f.name}>{f.label}</th>
                ))}
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <tr key={row.id}>
                  {colunasVisiveis.map((f) => (
                    <td key={f.name}>
                      {/* Competências só existem para cargo do Operacional. */}
                      {f.type === "skills" && !cargoEhDoOperacional(row.cargo, indiceCargos)
                        ? "—"
                        : f.cores && row[f.name]
                          ? (
                            <span className={`pill pill-${f.cores[String(row[f.name])] ?? "neutral"}`}>
                              {formatCell(f, row)}
                            </span>
                          )
                          : formatCell(f, row)}
                    </td>
                  ))}
                  <td className="col-actions">
                    {(tab.acoesLinha ?? []).map((a) => {
                      const meta = ACAO_META[a];
                      const Icon = meta.Icon;
                      return (
                        <button
                          key={a}
                          className={`icon-btn ${meta.cls}`}
                          title={meta.title}
                          onClick={() => setAcao({ tipo: a, row })}
                        >
                          <Icon size={17} />
                        </button>
                      );
                    })}
                    <button className="icon-btn" title="Editar" onClick={() => openEdit(row)}>
                      <IconEdit size={17} />
                    </button>
                    {podeExcluir && (
                      <button
                        className="icon-btn icon-btn-danger"
                        title="Excluir"
                        onClick={() => handleDelete(row)}
                      >
                        <IconTrash size={17} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {formOpen && (
        <RecordForm
          title={editing ? `Editar — ${tab.label}` : `Novo — ${tab.label}`}
          fields={tab.fields}
          initial={editing}
          colaboradores={colaboradores}
          indiceCargos={indiceCargos}
          onCancel={() => setFormOpen(false)}
          onSubmit={handleSubmit}
        />
      )}

      {acao?.tipo === "treinoIa" && (
        <SugerirTreinosModal
          colaborador={acao.row}
          onClose={() => setAcao(null)}
          onApplied={refreshAll}
        />
      )}
      {acao?.tipo === "pasta" && (
        <PastaModal colaborador={acao.row} onClose={() => setAcao(null)} />
      )}
      {acao?.tipo === "documentos" && (
        // Mesma tela da pasta, apontando para os arquivos da ENTREVISTA:
        // o candidato ainda não é colaborador e pode nem ser contratado.
        <PastaModal
          colaborador={acao.row}
          onClose={() => setAcao(null)}
          repo={api.documentosEntrevista}
          campoNome="candidato"
          titulo={`Documentos — ${acao.row.candidato ?? "candidato"}`}
          dica="Currículo, RG, CPF e comprovantes entregues pelo candidato."
        />
      )}
      {acao?.tipo === "convite" && (
        // O CONVITE do candidato: o link com dia, horário, endereço da SERCAMP
        // e quem procurar na recepção. Não fecha ao salvar — o RH ainda vai
        // copiar o link e mandar pelo WhatsApp na mesma tela.
        <ConviteEntrevistaModal
          entrevista={acao.row}
          onClose={() => setAcao(null)}
          onMudou={(msg) => setAvisoPlanilha(msg)}
        />
      )}
      {acao?.tipo === "retorno" && (
        // O RETORNO do processo seletivo (positivo ou negativo) pelo WhatsApp
        // do RH. Não mexe no status da entrevista: a listagem não recarrega.
        <RetornoEntrevistaModal
          entrevista={acao.row}
          onClose={() => setAcao(null)}
          onMudou={(msg) => setAvisoPlanilha(msg)}
        />
      )}
      {acao?.tipo === "roteiro" && (
        // O roteiro pode mudar o STATUS da entrevista (a recomendação do
        // parecer), então recarrega a listagem ao salvar.
        <RoteiroEntrevistaModal
          entrevista={acao.row}
          onClose={() => setAcao(null)}
          onSalvo={async (msg) => {
            setAcao(null);
            setAvisoPlanilha(msg);
            await refreshAll();
          }}
        />
      )}
      {acao?.tipo === "curriculo" && (
        // O currículo que o candidato mandou na TRIAGEM, aberto aqui: o mesmo
        // painel que aparece ao lado do roteiro durante a entrevista.
        <CurriculoCandidatoModal
          entrevista={acao.row}
          onClose={() => setAcao(null)}
          onMudou={async (msg) => {
            setAvisoPlanilha(msg);
            await refreshAll();
          }}
        />
      )}
      {acao?.tipo === "avaliacoes" && (
        <AvaliacoesColaboradorModal
          /*
            Na aba Avaliações a linha É uma avaliação: o histórico é o do
            colaborador dela (a lista traz colaborador_id e o nome pelo join).
          */
          colaborador={
            tab.resource === "avaliacoes"
              ? { id: acao.row.colaborador_id, nome: acao.row.colaborador, cargo: acao.row.cargo }
              : acao.row
          }
          onClose={() => setAcao(null)}
        />
      )}
      {acao?.tipo === "ficha" && (
        <FichaModal colaborador={acao.row} onClose={() => setAcao(null)} />
      )}
      {acao?.tipo === "concluir" && (
        <ConcluirTreinoModal
          treinamento={acao.row}
          onClose={() => setAcao(null)}
          onConcluded={refreshAll}
        />
      )}
      {acao?.tipo === "certificado" && (
        <CertificadoTreinoModal
          treinamento={acao.row}
          onClose={() => setAcao(null)}
          onChanged={refreshAll}
        />
      )}
      {acao?.tipo === "engajIa" && (
        <SugerirEngajamentoModal
          registro={acao.row}
          onClose={() => setAcao(null)}
          onSaved={refreshAll}
        />
      )}
      {gerarLink && (
        <GerarLinkAvaliacaoModal colaboradores={colaboradores} onClose={() => setGerarLink(false)} />
      )}
      {consolidado && <ConsolidadoAvaliacoesModal onClose={() => setConsolidado(false)} />}
      {(aplicarAvaliacao || acao?.tipo === "avaliar") && (
        <AplicarAvaliacaoModal
          colaboradores={colaboradores}
          colaboradorInicial={acao?.tipo === "avaliar" ? Number(acao.row.id) : null}
          onClose={() => {
            setAplicarAvaliacao(false);
            setAcao(null);
          }}
          onSalvo={async (msg) => {
            setAplicarAvaliacao(false);
            setAcao(null);
            setAvisoPlanilha(msg);
            await refreshAll();
          }}
        />
      )}
      {modelos && <ModelosPromocaoModal onClose={() => setModelos(false)} />}
      {escolherColunas && (
        <ColunasModal
          campos={tab.fields}
          escolhidas={colunas}
          padrao={padraoColunas}
          onAplicar={(nomes) => {
            definirColunas(nomes);
            setEscolherColunas(false);
          }}
          onClose={() => setEscolherColunas(false)}
        />
      )}
      {importar && (
        <ImportarColaboradoresModal
          onClose={() => setImportar(false)}
          onImportado={async (msg) => {
            setImportar(false);
            setAvisoPlanilha(msg);
            await refreshAll();
          }}
        />
      )}
    </div>
  );
}

/**
 * Escolha das colunas da listagem. Marcar/desmarcar não muda nada no banco —
 * é só o que a tabela mostra, e a preferência fica neste navegador.
 */
function ColunasModal({
  campos,
  escolhidas,
  padrao,
  onAplicar,
  onClose,
}: {
  campos: TabDef["fields"];
  escolhidas: string[];
  padrao: string[];
  onAplicar: (nomes: string[]) => void;
  onClose: () => void;
}) {
  const [marcadas, setMarcadas] = useState<string[]>(escolhidas);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  function alternar(nome: string) {
    setMarcadas((prev) =>
      prev.includes(nome) ? prev.filter((n) => n !== nome) : [...prev, nome],
    );
  }

  return (
    <div className="modal-overlay" onMouseDown={onClose}>
      <div className="modal-card colunas-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <div>
            <h3>Colunas da lista</h3>
            <p className="modal-sub">
              Marque o que quer ver na tabela. Todos os dados continuam no cadastro —
              isto muda só a exibição, neste navegador.
            </p>
          </div>
          <button type="button" className="icon-btn" onClick={onClose}>
            <IconClose size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="colunas-acoes">
            <button className="btn-secondary btn-sm" onClick={() => setMarcadas(campos.map((f) => f.name))}>
              Marcar todas
            </button>
            <button className="btn-secondary btn-sm" onClick={() => setMarcadas(padrao)}>
              Voltar ao padrão
            </button>
          </div>
          <div className="colunas-lista">
            {campos.map((f) => (
              <label key={f.name} className={`colunas-item ${marcadas.includes(f.name) ? "marcada" : ""}`}>
                <input
                  type="checkbox"
                  checked={marcadas.includes(f.name)}
                  onChange={() => alternar(f.name)}
                />
                <span>{f.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="modal-foot">
          <span className="muted">{marcadas.length} de {campos.length} coluna(s)</span>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={marcadas.length === 0}
            onClick={() => onAplicar(marcadas)}
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}
